import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { User, ReportingPeriod } from '../types';
import { authService, LoginCredentials, RegisterData, SyncResponse, normalizeRole } from '../services/authService';
import { organizationService, FacilityListItem } from '../services/organizationService';
import { supabase } from '../services/supabaseClient';

// Default reporting periods
const defaultReportingPeriods: ReportingPeriod[] = [
  { year: 2026, startDate: '2026-01-01', endDate: '2026-12-31', status: 'in_progress' },
  { year: 2025, startDate: '2025-01-01', endDate: '2025-12-31', status: 'approved' },
  { year: 2024, startDate: '2024-01-01', endDate: '2024-12-31', status: 'approved' },
  { year: 2023, startDate: '2023-01-01', endDate: '2023-12-31', status: 'approved' },
];

// View mode: single facility or entire organization
export type DashboardViewMode = 'facility' | 'organization';

interface AppContextType {
  user: User | null;
  facilities: FacilityListItem[];
  selectedFacility: FacilityListItem | null;
  selectedPeriod: ReportingPeriod | null;
  viewMode: DashboardViewMode;
  needsSetup: boolean;
  setSelectedFacility: (facility: FacilityListItem | null) => void;
  setSelectedPeriod: (period: ReportingPeriod) => void;
  setViewMode: (mode: DashboardViewMode) => void;
  refreshFacilities: () => Promise<void>;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  sendMagicLink: (email: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  // Backward compatibility
  selectedCompany: { id: string; name: string } | null;
  setSelectedCompany: (company: any) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [facilities, setFacilities] = useState<FacilityListItem[]>([]);
  const [selectedFacility, setSelectedFacility] = useState<FacilityListItem | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<ReportingPeriod | null>(defaultReportingPeriods[0]);
  const [viewMode, setViewMode] = useState<DashboardViewMode>('organization');
  const [needsSetup, setNeedsSetup] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Ref to prevent concurrent sync operations from racing
  const isSyncing = useRef(false);
  const initializedRef = useRef(false);

  // Fetch facilities when user is authenticated
  const fetchFacilities = useCallback(async (orgId: string) => {
    try {
      const response = await organizationService.listFacilities(orgId);
      setFacilities(response.facilities || []);
      if (response.facilities && response.facilities.length > 0 && !selectedFacility) {
        setSelectedFacility(response.facilities[0]);
      }
    } catch (err) {
      console.error('Failed to fetch facilities:', err);
    }
  }, [selectedFacility]);

  // Refresh facilities
  const refreshFacilities = useCallback(async () => {
    if (user?.companyId) {
      await fetchFacilities(user.companyId);
    }
  }, [user?.companyId, fetchFacilities]);

  // Sync user with backend - guarded against concurrent calls
  const syncAndSetUser = useCallback(async () => {
    if (isSyncing.current) return;
    isSyncing.current = true;

    try {
      const syncResponse = await authService.syncUser();
      const mappedUser = {
        id: syncResponse.user.id,
        email: syncResponse.user.email,
        name: syncResponse.user.name,
        role: normalizeRole(syncResponse.user.role as string),
        companyId: syncResponse.user.organization_id,
        organizationName: syncResponse.user.organization_name || undefined,
      };

      setUser(mappedUser);
      setNeedsSetup(syncResponse.needs_setup);

      if (mappedUser.companyId && !syncResponse.needs_setup) {
        await fetchFacilities(mappedUser.companyId);
      }
    } catch (err) {
      console.error('Failed to sync user:', err);
      setUser(null);
    } finally {
      isSyncing.current = false;
    }
  }, [fetchFacilities]);

  // Initialize auth and listen for changes
  useEffect(() => {
    // Prevent StrictMode double-initialization
    if (initializedRef.current) return;
    initializedRef.current = true;

    // Initialize auth from existing session
    const initAuth = async () => {
      try {
        const { data } = await supabase.auth.getSession();

        if (data.session) {
          try {
            const currentUser = await authService.getCurrentUser();
            if (currentUser) {
              setUser(currentUser);
              if (currentUser.companyId) {
                await fetchFacilities(currentUser.companyId);
              }
            }
          } catch {
            try {
              await syncAndSetUser();
            } catch {
              // Can't sync either - clear state
            }
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, _session) => {
        if (event === 'SIGNED_OUT') {
          setUser(null);
          setFacilities([]);
          setSelectedFacility(null);
          setNeedsSetup(false);
          setError(null);
          setIsLoading(false);
        }
        // SIGNED_IN is handled by login() and initAuth() directly
        // INITIAL_SESSION is handled by initAuth()
        // TOKEN_REFRESHED needs no action
      }
    );

    initAuth();

    return () => {
      subscription.unsubscribe();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for unauthorized events (401 responses)
  useEffect(() => {
    const handleUnauthorized = () => {
      setUser(null);
      setFacilities([]);
      setSelectedFacility(null);
      setError('Session expired. Please log in again.');
      setIsLoading(false);
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const credentials: LoginCredentials = { email, password };
      const { user: loggedInUser, needsSetup: setup } = await authService.login(credentials);
      setUser(loggedInUser);
      setNeedsSetup(setup);
      if (loggedInUser?.companyId && !setup) {
        await fetchFacilities(loggedInUser.companyId);
      }
    } catch (err: any) {
      const message = err?.data?.detail || err?.message || 'Login failed. Please try again.';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [fetchFacilities]);

  const loginWithGoogle = useCallback(async () => {
    setError(null);
    try {
      await authService.loginWithGoogle();
      // Redirect happens - no state update needed
    } catch (err: any) {
      const message = err?.message || 'Google login failed.';
      setError(message);
      throw err;
    }
  }, []);

  const sendMagicLink = useCallback(async (email: string) => {
    setError(null);
    try {
      await authService.sendMagicLink(email);
    } catch (err: any) {
      const message = err?.message || 'Failed to send magic link.';
      setError(message);
      throw err;
    }
  }, []);

  const register = useCallback(async (data: RegisterData) => {
    setIsLoading(true);
    setError(null);

    try {
      const { user: registeredUser, needsSetup: setup } = await authService.register(data);
      setUser(registeredUser);
      setNeedsSetup(setup);
    } catch (err: any) {
      const message = err?.data?.detail || err?.message || 'Registration failed. Please try again.';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      await authService.logout();
    } finally {
      setUser(null);
      setFacilities([]);
      setSelectedFacility(null);
      setSelectedPeriod(defaultReportingPeriods[0]);
      setNeedsSetup(false);
      setError(null);
      setIsLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Backward compatibility: selectedCompany maps to organization info from user
  const selectedCompany = user ? {
    id: user.companyId,
    name: 'Organization',
  } : null;

  return (
    <AppContext.Provider
      value={{
        user,
        facilities,
        selectedFacility,
        selectedPeriod,
        viewMode,
        needsSetup,
        setSelectedFacility,
        setSelectedPeriod,
        setViewMode,
        refreshFacilities,
        isAuthenticated: !!user,
        isLoading,
        error,
        login,
        loginWithGoogle,
        sendMagicLink,
        register,
        logout,
        clearError,
        // Backward compatibility
        selectedCompany,
        setSelectedCompany: () => {},
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};
